// src/App.js

import { useEffect } from 'react';
import {
  BrowserRouter as Router,
  Routes,
  Route,
  Navigate,
  useLocation,
  useNavigate,
  useParams,
} from "react-router-dom";
import { Bounce, ToastContainer } from "react-toastify";
import 'react-toastify/dist/ReactToastify.css';

import { RequireAuth } from "./components/RequireAuth";
import ThemeWrapper from "./ui/widgets/ThemeWrapper";

// Auth
import AuthEntryScreen from "./ui/auth/AuthEntryScreen";
import PhoneAuthPage from "./ui/auth/PhoneAuthPage";
import GooglePhoneNumberPage from "./ui/auth/GooglePhoneNumberPage";
import CountryPickerPage from "./ui/auth/CountryPickerPage";
import LoginOtpPage from "./ui/auth/LoginOtpPage";
import OtpVerificationPage from "./ui/auth/OtpVerificationPage";
import SignInPage from "./ui/auth/SignInPage";
import AddProfilePage from "./ui/auth/AddProfilePage";
import TermsOfService from "./ui/auth/TermsOfService";
import PrivacyPolicy from "./ui/auth/PrivacyPolicy";

// Home / Profile
import HomeScreen from "./ui/screens/HomeScreen";
import ProfileScreen from "./ui/screens/ProfileScreen";
import EditProfileScreen from "./ui/screens/EditProfileScreen";
import NotificationsScreen from "./ui/screens/NotificationsScreen";
import FullProfileImageScreen from "./ui/screens/FullProfileImageScreen";
import SubscribersSubscribedScreen from "./ui/screens/SubscribersSubscribedScreen";

// Chats
import ConversationsScreen from "./ui/screens/chats/ConversationsScreen";
import ChatScreen from "./ui/screens/chats/ChatScreen";
import ArchiveScreen from "./ui/screens/chats/ArchiveScreen";
import ForwardContactSelector from "./ui/screens/chats/ForwardContactSelector";
import NewContactScreen from "./ui/screens/NewContactScreen";
import SelectContactScreen from "./ui/screens/SelectContactScreen";
import FirebaseNewChatScreen from "./ui/screens/FirebaseNewChatScreen";

// Post / Listings
import PostScreen from "./ui/screens/post/PostScreen";
import CreateAdScreen from "./ui/screens/post/CreateAdScreen";
import AddPropertyPage from "./ui/screens/post/AddPropertyPage";
import EditListingPage from "./ui/screens/post/EditListingPage";
import ListingPage from "./ui/screens/post/ListingPage";
import ListingImagesPage from "./ui/screens/post/ListingImagesPage";
import AdDetailsScreen from "./ui/screens/post/AdDetailsScreen";
import AtomViewScreen from "./ui/screens/post/AtomViewScreen";
import CategoryPickerPage from "./ui/screens/post/CategoryPickerPage";
import DistrictPickerPage from "./ui/screens/post/DistrictPickerPage";
import DivisionPickerPage from "./ui/screens/post/DivisionPickerPage";
import SearchScreen from "./ui/screens/post/SearchScreen";
import AllUsersScreen from "./ui/screens/post/AllUsersScreen";
import AddStatusScreen from './ui/screens/post/AddStatusScreen';
import StatusViewerScreen from './ui/screens/post/StatusScreen';
import AddWantScreen from './ui/screens/post/AddWantScreen';
import WantViewerScreen from './ui/screens/post/WantScreen';

// Settings
import SettingsScreen from "./ui/screens/settings/SettingsScreen";
import AboutScreen from "./ui/screens/settings/AboutScreen";
import ContactUsScreen from "./ui/screens/settings/ContactUsScreen";
import QrDisplayScreen from "./ui/screens/settings/QrDisplayScreen";
import OptimizeAppScreen from "./ui/screens/settings/OptimizeAppScreen";
import IdVerificationPage from "./ui/screens/verifications/IdVerificationPage";
import PendingVerificationPage from "./ui/screens/verifications/PendingVerificationPage";
import RejectedVerificationPage from "./ui/screens/verifications/RejectedVerificationPage";
import VerificationManagementPage from "./ui/screens/verifications/VerificationManagementPage";
import CategorySelectionPage from "./ui/screens/verifications/CategorySelectionPage";

// Affiliation
import AffiliationScreen from "./ui/screens/affiliation/AffiliationScreen";

// Custom Scroll Restoration Hook
function useScrollRestoration() {
  const location = useLocation();

  useEffect(() => {
    const saveScrollPosition = () => {
      sessionStorage.setItem(`scrollPos:${location.pathname}`, window.scrollY.toString());
    };

    const scrollPos = sessionStorage.getItem(`scrollPos:${location.pathname}`);
    if (scrollPos) {
      window.scrollTo(0, parseInt(scrollPos));
    }

    return () => {
      saveScrollPosition();
    };
  }, [location.pathname]);
}

// Wrapper component to provide location context to the hook
function ScrollRestorationWrapper({ children }) {
  useScrollRestoration();
  return children;
}

// ── Route wrappers for the viewers ───────────────────────────────────────
//
// PostScreen mounts StatusViewerScreen / WantViewerScreen as overlays and
// passes props. When they're opened directly by URL (/status/:uid,
// /want/:uid) there is no parent to pass them, so these wrappers supply
// the same props from the route.

function closeOrHome(navigate) {
  if (window.history.length > 1) navigate(-1);
  else navigate('/');
}

function StatusViewerRoute() {
  const { uid } = useParams();
  const navigate = useNavigate();
  return (
    <StatusViewerScreen
      uid={uid}
      onClose={() => closeOrHome(navigate)}
      onOpenProfile={(id) => navigate(`/profile/${id}`)}
    />
  );
}

function WantViewerRoute() {
  const { uid } = useParams();
  const navigate = useNavigate();
  return (
    <WantViewerScreen
      uid={uid}
      onClose={() => closeOrHome(navigate)}
      onOpenProfile={(id) => navigate(`/profile/${id}`)}
      onOpenChat={(payload) =>
        navigate(`/chat/${payload.conversationId}`, { state: payload })
      }
    />
  );
}

// /chats/:conversationId → /chat/:conversationId, keeping the router state
// (receiverId, title, initialMessage, …) that the Want viewer sends along.
function ChatsRedirect() {
  const { chatId } = useParams();
  const location = useLocation();
  return <Navigate to={`/chat/${chatId}`} replace state={location.state} />;
}

function App() {
  return (
    <ThemeWrapper>
      <Router>
        <ScrollRestorationWrapper>
          <Routes>
            {/* Home — owns its own BottomNavBar internally for its
                Conversations / Post / Settings tabs, and now guards the
                Conversations and Settings tabs with RequireAuth itself. */}
            <Route path="/" element={<HomeScreen />} />

            {/* Standalone public URL — filatom.com/privacy-policy — kept
                separate from /auth/privacy-policy so the auth flow's own
                link keeps working unchanged; both render the same page. */}
            <Route path="/privacy-policy" element={<PrivacyPolicy />} />

            {/* Auth */}
            <Route path="/auth" element={<AuthEntryScreen />} />
            <Route path="/auth/phone" element={<PhoneAuthPage />} />
            <Route path="/auth/google-phone" element={<GooglePhoneNumberPage />} />
            <Route path="/auth/country" element={<CountryPickerPage />} />
            <Route path="/auth/login-otp" element={<LoginOtpPage />} />
            <Route path="/auth/verify-otp" element={<OtpVerificationPage />} />
            <Route path="/auth/sign-in" element={<SignInPage />} />
            <Route path="/auth/add-profile" element={<AddProfilePage />} />
            <Route path="/auth/terms-of-service" element={<TermsOfService />} />
            <Route path="/auth/privacy-policy" element={<PrivacyPolicy />} />

            {/* Profile */}
            <Route path="/profile" element={<RequireAuth><ProfileScreen /></RequireAuth>} />
            <Route path="/profile/:userId" element={<ProfileScreen />} />
            <Route path="/profile/:userId/image" element={<FullProfileImageScreen />} />
            <Route path="/profile/:userId/subscribers" element={<SubscribersSubscribedScreen />} />
            <Route path="/edit-profile" element={<RequireAuth><EditProfileScreen /></RequireAuth>} />
            <Route path="/notifications" element={<RequireAuth><NotificationsScreen /></RequireAuth>} />

            {/* Chats */}
            <Route path="/chat" element={<RequireAuth><ConversationsScreen /></RequireAuth>} />
            <Route path="/chat/archive" element={<RequireAuth><ArchiveScreen /></RequireAuth>} />
            <Route path="/chat/:chatId" element={<RequireAuth><ChatScreen /></RequireAuth>} />
            <Route path="/chat/:chatId/forward" element={<RequireAuth><ForwardContactSelector /></RequireAuth>} />
            {/* PostScreen's Want overlay navigates to /chats/:id */}
            <Route path="/chats/:chatId" element={<RequireAuth><ChatsRedirect /></RequireAuth>} />
            <Route path="/new-chat" element={<RequireAuth><FirebaseNewChatScreen /></RequireAuth>} />
            <Route path="/new-contact" element={<RequireAuth><NewContactScreen /></RequireAuth>} />
            <Route path="/select-contact" element={<RequireAuth><SelectContactScreen /></RequireAuth>} />

            {/* Post / Listings */}
            <Route path="/post" element={<RequireAuth><PostScreen /></RequireAuth>} />
            <Route path="/post/create" element={<RequireAuth><CreateAdScreen /></RequireAuth>} />
            <Route path="/post/add-property" element={<RequireAuth><AddPropertyPage /></RequireAuth>} />
            <Route path="/post/category" element={<RequireAuth><CategoryPickerPage /></RequireAuth>} />
            <Route path="/post/district" element={<RequireAuth><DistrictPickerPage /></RequireAuth>} />
            <Route path="/post/division" element={<RequireAuth><DivisionPickerPage /></RequireAuth>} />
            <Route path="/search" element={<SearchScreen />} />
            <Route path="/users" element={<RequireAuth><AllUsersScreen /></RequireAuth>} />
            <Route path="/listing/:id" element={<ListingPage />} />
            <Route path="/listing/:id/images" element={<ListingImagesPage />} />
            <Route path="/listing/:id/details" element={<AdDetailsScreen />} />
            <Route path="/listing/:id/edit" element={<RequireAuth><EditListingPage /></RequireAuth>} />
            <Route path="/atom/:atomId" element={<AtomViewScreen />} />

            {/* Add Status / Add Want — PostScreen and PostAddFab navigate to
                /post/add-status and /post/add-want, and AddWantScreen's
                pickers return to /post/add-want, so both path styles are
                registered. */}
            <Route path="/post/add-status" element={<RequireAuth><AddStatusScreen /></RequireAuth>} />
            <Route path="/post/add-want" element={<RequireAuth><AddWantScreen /></RequireAuth>} />
            <Route path="/status/add" element={<RequireAuth><AddStatusScreen /></RequireAuth>} />
            <Route path="/want/add" element={<RequireAuth><AddWantScreen /></RequireAuth>} />

            {/* Viewers opened directly by URL */}
            <Route path="/status/:uid" element={<RequireAuth><StatusViewerRoute /></RequireAuth>} />
            <Route path="/want/:uid" element={<RequireAuth><WantViewerRoute /></RequireAuth>} />

            {/* Settings */}
            <Route path="/settings" element={<RequireAuth><SettingsScreen /></RequireAuth>} />
            <Route path="/settings/about" element={<AboutScreen />} />
            <Route path="/settings/contact-us" element={<ContactUsScreen />} />
            <Route path="/settings/qr" element={<RequireAuth><QrDisplayScreen /></RequireAuth>} />
            <Route path="/settings/optimize-app" element={<RequireAuth><OptimizeAppScreen /></RequireAuth>} />
            <Route path="/settings/affiliation" element={<RequireAuth><AffiliationScreen /></RequireAuth>} />
            <Route path="/verifications" element={<RequireAuth><VerificationManagementPage /></RequireAuth>} />
            <Route path="/verifications/id" element={<RequireAuth><IdVerificationPage /></RequireAuth>} />
            <Route path="/verifications/category-selection" element={<RequireAuth><CategorySelectionPage /></RequireAuth>} />
            <Route path="/verifications/pending" element={<RequireAuth><PendingVerificationPage /></RequireAuth>} />
            <Route path="/verifications/rejected" element={<RequireAuth><RejectedVerificationPage /></RequireAuth>} />
          </Routes>
        </ScrollRestorationWrapper>
      </Router>
      <ToastContainer
        position="bottom-center"
        autoClose={5000}
        hideProgressBar={false}
        newestOnTop={false}
        closeOnClick={false}
        rtl={false}
        pauseOnFocusLoss
        draggable
        pauseOnHover
        theme="dark"
        transition={Bounce}
      />
    </ThemeWrapper>
  );
}

export default App;